import {
  Button,
  ButtonGroup,
  Separator,
  Flex,
  IconButton,
  Popover,
  Text,
  useDisclosure,
  NativeSelect,
  Icon,
} from "@chakra-ui/react";
import { t } from "i18next";
import { observer } from "mobx-react";
import { ChangeEvent, useState, useEffect } from "react";
import { SelectFilter } from "./SelectFilter";
import { FilterTag } from "../FilterTag";
import { TextAreaFilter } from "./TextAreaFilter";
import {
  AttributeDefinition,
  AttributeDefinitions,
  AttributeName,
  AttributeType,
} from "@keita/lanepatrol-sdk";
import { AttributeUtils } from "../../../../../coding/utils/attribute-utils";
import {
  craAttributeFilterToString,
  NumberAttributeFilter,
  CraAttributeFilter,
  CraAttributeFilterType,
  SelectAttributeFilter,
  TextAreaAttributeFilter,
} from "../../../../../map/models/MapFilters";
import { useStores } from "../../../../../shared/stores/RootStore";
import { CustomPopover } from "../../../../../shared/views/CustomPopover";
import { TextFilter } from "./TextFilter";
import { DeleteIcon } from "../../../../../shared/views/icons";
import { AddFilterButton } from "../AddFilterButton";

const CraAttributeFiltersBase: React.FC = () => {
  const { filterStore } = useStores();
  const { open, onToggle, onClose } = useDisclosure();
  const [newFilters, setNewFilters] = useState<CraAttributeFilter[]>([]);

  useEffect(() => {
    if (open) {
      setNewFilters([...filterStore.craAttributeFilters]);
    }
    //eslint-disable-next-line
  }, [open]);

  const handleAddFilter = (e: ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value as AttributeName;
    const attribute = AttributeDefinitions.getByName(value)!;
    const filter = getDefaultFilter(attribute);
    setNewFilters((prev) => {
      return prev.concat([filter]);
    });
  };

  const handleClear = () => {
    setNewFilters([]);
  };

  const handleSubmit = () => {
    filterStore.setAttributeFilters(
      newFilters.filter((filter) => {
        if (filter.type === CraAttributeFilterType.Number) {
          const f = filter as NumberAttributeFilter;
          if (!f.min && !f.max) return false;
        } else if (filter.type === CraAttributeFilterType.Select) {
          const f = filter as SelectAttributeFilter;
          if (!f.value) return false;
        }
        return true;
      }),
    );
    onClose();
  };

  const handleChangeFilter = (filterIndex: number, data: any) => {
    setNewFilters((prev) =>
      prev.map((f, i) => (i === filterIndex ? { ...f, ...data } : f)),
    );
  };

  const handleRemoveFilter = (filterIndex: number) => {
    setNewFilters((prev) => prev.filter((_, i) => i !== filterIndex));
  };

  const removeActiveFilter = (attrName: string) => {
    const newFilters = filterStore.craAttributeFilters.filter(
      (filter) => filter.attributeName !== attrName,
    );
    filterStore.setAttributeFilters(newFilters);
  };

  return (
    <>
      <CustomPopover
        open={open}
        onClose={onClose}
        maxH="360px"
        minW="360px"
        trigger={<AddFilterButton label="Attribute" onClick={onToggle} />}
      >
        <Popover.Header fontWeight={"semibold"}>
          {t("map:filters.byAttributeTitle")}
        </Popover.Header>
        <Popover.Body overflowY="auto">
          <div>
            <NativeSelect.Root mt={2} variant="outline">
              <NativeSelect.Field
                placeholder="Add attribute filter"
                onChange={handleAddFilter}
                value={""}
              >
                {AttributeDefinitions.all.map((attribute, i) => (
                  <option key={i} value={attribute.name}>
                    {AttributeUtils.getLabel(attribute.name)}
                  </option>
                ))}
              </NativeSelect.Field>
              <NativeSelect.Indicator />
            </NativeSelect.Root>
          </div>
          <div>
            {[...newFilters].map((filter, i) => {
              const attributeDefinition = AttributeDefinitions.getByName(
                filter.attributeName,
              )!;
              return (
                <div key={i}>
                  <Separator mt={4} />
                  <Flex alignItems="center" mt={2} mb={2}>
                    <Text fontSize={"sm"} flexGrow={1}>
                      {AttributeUtils.getLabel(attributeDefinition.name)}
                    </Text>
                    <IconButton
                      variant="ghost"
                      colorPalette="primary"
                      onClick={() => handleRemoveFilter(i)}
                      size="sm"
                      aria-label="delete"
                    >
                      <Icon as={DeleteIcon} />
                    </IconButton>
                  </Flex>
                  {getFilteringComponent(filter, (data: any) =>
                    handleChangeFilter(i, data),
                  )}
                </div>
              );
            })}
          </div>
        </Popover.Body>
        <Popover.Footer display="flex" justifyContent="flex-end">
          <ButtonGroup colorPalette={"primary"} size={"sm"}>
            <Button onClick={handleClear} disabled={!newFilters.length}>
              {t("map:filters.clearButton")}
            </Button>
            <Button onClick={handleSubmit}>
              {t("map:filters.submitButton")}
            </Button>
          </ButtonGroup>
        </Popover.Footer>
      </CustomPopover>

      {!!filterStore.craAttributeFilters.length &&
        filterStore.craAttributeFilters.map((attributeFilter, i) => {
          return (
            <FilterTag
              key={i}
              onClose={() => removeActiveFilter(attributeFilter.attributeName)}
            >
              {`${AttributeUtils.getLabel(
                attributeFilter.attributeName,
              )}: ${craAttributeFilterToString(attributeFilter)}`}
            </FilterTag>
          );
        })}
    </>
  );
};

export const CraAttributeFilters = observer(CraAttributeFiltersBase);

const getDefaultFilter = (
  attribute: AttributeDefinition,
): CraAttributeFilter => {
  switch (attribute.type) {
    case AttributeType.Number:
      const filter: NumberAttributeFilter = {
        attributeName: attribute.name,
        type: CraAttributeFilterType.Number,
        min: null,
        max: null,
      };
      return filter;

    case AttributeType.TextArea:
      return {
        attributeName: attribute.name,
        type: CraAttributeFilterType.TextArea,
        value: true,
      } as TextAreaAttributeFilter;

    case AttributeType.Select:
      return {
        attributeName: attribute.name,
        type: CraAttributeFilterType.Select,
      } as SelectAttributeFilter;

    default:
      throw new Error("Filtering is unavailable for the requested attribute.");
  }
};

const getFilteringComponent = (
  filter: CraAttributeFilter,
  setValue: (data: any) => void,
): React.ReactNode => {
  if (filter.type === CraAttributeFilterType.Number) {
    return (
      <TextFilter
        attributeFilter={filter as NumberAttributeFilter}
        setValue={setValue}
      />
    );
  } else if (filter.type === CraAttributeFilterType.TextArea) {
    return (
      <TextAreaFilter
        attributeFilter={filter as TextAreaAttributeFilter}
        setValue={setValue}
      />
    );
  } else {
    return (
      <SelectFilter
        attributeFilter={filter as SelectAttributeFilter}
        setValue={setValue}
      />
    );
  }
};
